import type { DrawContext } from './draw';
import type { View } from './project';
import { toScreen } from './project';
import { fanClass, pairKey } from './open-fan';
import type { FanNode } from './open-fan';

/*
 * THE NAME UNDER THE POINTER — a ring, and its ties drawn quietly.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawFrame` lines 1882–1918.
 *
 * THE QUIETER SIBLING OF `open-fan.ts`, AND THE POINT IS THE DIFFERENCE IN VOLUME. Opening a name
 * is a decision; hovering one is a glance. So this says the same thing at about half the strength,
 * and a reader moving the pointer across a crowded map gets an answer without the map changing
 * character under them. Everything it needs — the classes, the tie key, the bend — comes from
 * `open-fan.ts`, so the two cannot drift apart on what a walked tie IS.
 *
 * BUT IT DOES NOT USE `strand`, AND THAT IS THE FINDING OF THIS UNIT. It reads like it should:
 * theirs is `strand` with six numbers changed. Calling `strand` here would be the deviation, not
 * the tidy-up, and the differences are not cosmetic:
 *
 *   the colours    242,236,220 → 224,217,200 → 206,201,186, all cooler than `strand`'s
 *   the stops      0.18 and 0.64, against 0.16 and 0.62 — the light gathers slightly later
 *   the refusal    a single length test, `len < gap + 16`, rather than one against BOTH gaps
 *   the far end    a near tie runs all the way to 1: it TOUCHES the far disc, where `strand` trims
 *   no span test   `strand` also refuses a visible span under 8%; this one does not
 *
 * The last two are the ones that show. A hover strand reaching its far end is what makes the glance
 * read as "and these", pointing at the names rather than stopping short of them.
 */

/** Their turn, in radians, exactly as written in the reference. */
const TAU = 6.2832;

/** What the reader has walked, as the hover needs it. Their `edgeRef` plays no part here. */
export interface HoverFog {
  /** Whether the far end has been opened. */
  walked: (id: number) => boolean;
  /** Whether the far end has been passed near enough to be recognised. */
  seen: (id: number) => boolean;
  /** The ties inferred rather than stated, keyed `min:max`. */
  weak?: ReadonlySet<string>;
}

/** How far a figure's disc may be drawn, in screen pixels. `open-fan.ts`'s floor and ceiling. */
const DISC_MIN = 4;
const DISC_MAX = 46;

/** How far the curve bows away from the straight line. The whole web's bend. */
const BEND = 0.13;

/** The ring around the name under the pointer. Look. */
const RING_COLOUR = 'rgba(226,238,255,.75)';
const RING_WIDTH = 1.2;
const RING_OUT = 7;

/** The air a hover strand leaves around the name — less than the open name's twenty. */
const GAP_AIR = 14;

/** Under this much left over, there is no strand worth drawing. */
const MIN_SPARE = 16;

/** How far a rumour runs before it fades: a leading stub, not a rule across the screen. */
const FAR_SHARE = 0.42;
const FAR_CAP = 0.95;

/** The two head alphas — a rumour, and everything else. About half the open fan's. */
const HEAD_FAR = 0.16;
const HEAD_NEAR = 0.27;

/** And the two widths. */
const WIDTH_FAR = 0.8;
const WIDTH_NEAR = 1.1;

/** The colours a hover strand gathers through, and where it gathers. Cooler than `strand`'s. */
const HOVER_HEAD = '242,236,220';
const HOVER_BODY = '224,217,200';
const HOVER_TAIL = '206,201,186';
const STOP_HEAD = 0.18;
const STOP_BODY_FAR = 0.5;
const STOP_BODY_NEAR = 0.64;
/** How much of the head alpha the body keeps. */
const BODY_SHARE = 0.42;

/** A point along the quadratic. Theirs, inlined here exactly as it is inlined there. */
function qp(u: number, p: number, c: number, q: number): number {
  const k = 1 - u;
  return k * k * p + 2 * k * u * c + u * u * q;
}

/** One tie, once its ends are on screen. */
interface Strand {
  cls: number;
  ox: number;
  oy: number;
  cx: number;
  cy: number;
}

/**
 * Draw the hovered name's ring and its ties. Returns how many strands landed.
 *
 * The ring is drawn BEFORE the ties, so a tie crosses over it rather than under — which is what
 * makes the ties read as leaving the name rather than as passing behind it.
 */
export function drawHoverFan(
  ctx: DrawContext,
  view: View,
  nodes: readonly FanNode[],
  hovered: number,
  edges: readonly number[],
  fog: HoverFog,
): number {
  const n = nodes[hovered];
  if (n === undefined) return 0;
  const z = view.cam.z;
  const s = toScreen(view, n.x, n.y);
  const r = Math.max(DISC_MIN, Math.min(DISC_MAX, n.r * z));
  ctx.strokeStyle = RING_COLOUR;
  ctx.lineWidth = RING_WIDTH;
  ctx.beginPath();
  ctx.arc(s.x, s.y, r + RING_OUT, 0, TAU);
  ctx.stroke();

  const weak = fog.weak;
  const seen = new Set<number>();
  const strands: Strand[] = [];
  for (const id of edges) {
    if (seen.has(id)) continue;
    seen.add(id);
    const o = nodes[id];
    /* Streamed, so a tie can name an end that has not landed yet. Theirs would throw. */
    if (o === undefined) continue;
    const soft = weak?.has(pairKey(hovered, id)) === true;
    const cls = fanClass(fog.walked(id), fog.seen(id), soft);
    const os = toScreen(view, o.x, o.y);
    const cs = toScreen(view, (n.x + o.x) * 0.5 * (1 - BEND), (n.y + o.y) * 0.5 * (1 - BEND));
    strands.push({ cls, ox: os.x, oy: os.y, cx: cs.x, cy: cs.y });
  }

  const gap = r + GAP_AIR;
  let drawn = 0;
  for (const v of strands) {
    const far = v.cls === 2;
    const len = Math.hypot(v.ox - s.x, v.oy - s.y);
    /* One test against ONE gap, unlike `strand`, which counts the air at both ends. */
    if (len < gap + MIN_SPARE) continue;
    const u0 = gap / len;
    /* A near tie runs all the way to its far end and touches the disc there. Theirs, and it is
       what makes the glance point AT the names rather than stop short of them. */
    const u1 = far ? Math.min(FAR_CAP, u0 + FAR_SHARE) : 1;
    const p0x = qp(u0, s.x, v.cx, v.ox);
    const p0y = qp(u0, s.y, v.cy, v.oy);
    const p1x = qp(u1, s.x, v.cx, v.ox);
    const p1y = qp(u1, s.y, v.cy, v.oy);
    /* The control point that makes the TRIMMED span follow the original curve. */
    const cmx = qp((u0 + u1) / 2, s.x, v.cx, v.ox) * 2 - (p0x + p1x) / 2;
    const cmy = qp((u0 + u1) / 2, s.y, v.cy, v.oy) * 2 - (p0y + p1y) / 2;
    const head = far ? HEAD_FAR : HEAD_NEAR;
    const g = ctx.createLinearGradient(p0x, p0y, p1x, p1y);
    g.addColorStop(0, `rgba(${HOVER_HEAD},0)`);
    g.addColorStop(STOP_HEAD, `rgba(${HOVER_HEAD},${head.toFixed(3)})`);
    g.addColorStop(far ? STOP_BODY_FAR : STOP_BODY_NEAR, `rgba(${HOVER_BODY},${(head * BODY_SHARE).toFixed(3)})`);
    g.addColorStop(1, `rgba(${HOVER_TAIL},0)`);
    ctx.strokeStyle = g;
    ctx.lineWidth = far ? WIDTH_FAR : WIDTH_NEAR;
    ctx.beginPath();
    ctx.moveTo(p0x, p0y);
    ctx.quadraticCurveTo(cmx, cmy, p1x, p1y);
    ctx.stroke();
    drawn += 1;
  }
  return drawn;
}
